// Dashboard.jsx
import { Row, Spin } from "antd";
import { useEffect, useRef, useState } from 'react';
import Resource from '../Resources/Resources';
import useTopbarTitleHooks from "hooks/useTopbarTitleHooks";
import PaymentStatusBanner from './PaymentStatusBanner';
import WorkspaceSetupCard from './WorkspaceSetupCard';
import useOrgRepo from 'services/repo/useOrgRepo';
import { getSelectedTenant } from 'services/tenant/tenantMembershipService';


export default function Dashboard() {
    useTopbarTitleHooks("Home");
    const organizationCheckStarted = useRef(false);
    const [isCheckingOrganization, setIsCheckingOrganization] = useState(true);
    const [needsOrganizationSetup, setNeedsOrganizationSetup] = useState(false);
    const { getOrgList } = useOrgRepo(false);
    const workspace = getSelectedTenant();
    const isMockWorkspace = workspace?.source === 'onboarding-mock';
    const isUiDemoWorkspace = workspace?.source === 'ui-demo';
    const isOrganizationAdmin = isMockWorkspace || String(workspace?.role || '').trim().toLowerCase() === 'organization administrator';
    const showProgressiveSetup = isOrganizationAdmin && (isMockWorkspace || isUiDemoWorkspace || needsOrganizationSetup);
    const showExplorationPreview = showProgressiveSetup || isUiDemoWorkspace;

    useEffect(() => {
        if (!isOrganizationAdmin || isUiDemoWorkspace) {
            if (isUiDemoWorkspace && isOrganizationAdmin && workspace?.workspaceSetupStatus !== 'COMPLETE') {
                setNeedsOrganizationSetup(true);
            }
            setIsCheckingOrganization(false);
            return;
        }
        if (organizationCheckStarted.current) return;

        organizationCheckStarted.current = true;

        if (isMockWorkspace && workspace?.workspaceSetupStatus !== 'COMPLETE') {
            setNeedsOrganizationSetup(true);
            setIsCheckingOrganization(false);
            return;
        }

        const checkOrganization = async () => {
            try {
                const response = await getOrgList({
                    pageNumber: 1,
                    pageSize: 1,
                    orderBy: 'orgName',
                    isAscending: true,
                    filter: { orgName: '', orgContactPerson: '', orgContactNo: '', status: '' },
                });

                if (response?.status === 200 && response?.detail === 'OK' && Number(response?.data?.totalElements || 0) === 0) {
                    setNeedsOrganizationSetup(true);
                }
            } finally {
                setIsCheckingOrganization(false);
            }
        };

        checkOrganization();
    }, [getOrgList, isMockWorkspace, isOrganizationAdmin, isUiDemoWorkspace, workspace]);

    if (isCheckingOrganization && isOrganizationAdmin) {
        return <div className="dashboard-initial-check"><Spin size="large" tip="Preparing your organization..." /></div>;
    }

    return (
        <div className='dashboard-container'>
            <PaymentStatusBanner />
            {showProgressiveSetup
                ? <WorkspaceSetupCard workspace={workspace} />
                : null}
            {showExplorationPreview ? (
                <section className="dashboard-exploration">
                    <div className="dashboard-exploration__heading">
                        <div><span>EXPLORE WORKPLANSIQ</span><h2>Your Dashboard is ready</h2></div>
                        <p>Complete organization setup when you need live workforce insights. You can review the available capabilities now.</p>
                    </div>
                    <div className="dashboard-exploration__grid">
                        <article><strong>Workload Analysis</strong><span>Measure demand, capacity, and workload balance.</span><small>Available after organization setup</small></article>
                        <article><strong>Organization Structure</strong><span>Build divisions, departments, positions, and reporting lines.</span><small>Continue setup at any time</small></article>
                        <article><strong>Workforce Planning</strong><span>Turn operational evidence into defensible headcount plans.</span><small>Included with your selected plan</small></article>
                    </div>
                </section>
            ) : (
                <Row gutter={[16, 16]} className="dashboard-resource-preview">
                    <Resource />
                </Row>
            )}
        </div>
    )
}
